// src/components/admin/tabs/pointsCenter/PointsRankingPane.jsx
// 积分中心 · 排行与明细：维度切换 + 搜索 + 排行表（抽卡率/sparkline）+ 右侧明细抽屉。
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, RefreshCw, Search, X } from "lucide-react";
import {
  adjustUserPoints,
  fetchPointLedger,
  fetchPointStats,
  fetchPointUserDetail,
  grantUserPoints,
} from "../../../../services/adminService";
import { displayUserId, formatMoney } from "../../adminUtils";
import {
  EmptyBlock,
  EmptyText,
  ErrorNotice,
  FilterInput,
  IconAction,
  Panel,
  PrimaryButton,
  SecondaryButton,
  Segmented,
  StatusBadge,
} from "../../adminUi.jsx";
import { DateRangeControl, Sparkline } from "./pointsShared.jsx";
import {
  ACCENT,
  formatDateTime,
  formatPoints,
  formatRate,
  rangeOfPreset,
} from "./pointsShared.js";

const DIMENSIONS = [
  { key: "user", label: "按用户" },
  { key: "project", label: "按项目" },
  { key: "type", label: "按类型" },
];

const TYPE_LABELS = { image: "图片", video: "视频", text: "文本", audio: "音频" };

const LEDGER_LABELS = {
  GENERATION_CHARGE: "生成扣除",
  INITIAL_GRANT: "初始积分",
  ONE_TIME_GRANT: "一次性发放",
  ADMIN_ADJUSTMENT: "余额校正",
  RECURRING_GRANT: "周期发放",
  DAILY_QUOTA_RESET: "每日额度重置",
  POINT_BUCKET_MIGRATION: "积分账户分桶",
};

const formatSignedPoints = (value) => {
  const n = Number(value || 0);
  return `${n > 0 ? "+" : ""}${formatPoints(n)}`;
};

// ============================
// 明细抽屉
// ============================

const RankingDrawer = ({ target, range, projectNameOf, onClose }) => {
  const { dimension, key, name } = target;
  const isUser = dimension === "user";
  const scopeFilter = isUser ? { userId: key } : (dimension === "project" ? { projectId: key } : {});

  const [breakdown, setBreakdown] = useState(null);
  const [account, setAccount] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [grantForm, setGrantForm] = useState(() => ({ amount: "", note: "", requestId: crypto.randomUUID() }));
  const [adjustForm, setAdjustForm] = useState(() => ({ delta: "", note: "", requestId: crypto.randomUUID() }));

  const loadLedger = useCallback(async (nextPage) => {
    const data = await fetchPointLedger({ ...scopeFilter, from: range.from, to: range.to, page: nextPage, size: 20 });
    setLedger(data || { content: [], totalElements: 0, totalPages: 0 });
  }, [range.from, range.to, dimension, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setMessage("");
    setBreakdown(null);
    setAccount(null);
    setLedger(null);
    setPage(0);
    (async () => {
      try {
        const tasks = [
          fetchPointStats({ from: range.from, to: range.to, granularity: "day", dimension: "type", ...scopeFilter }),
          fetchPointLedger({ ...scopeFilter, from: range.from, to: range.to, page: 0, size: 20 }),
        ];
        if (isUser) tasks.push(fetchPointUserDetail(key, 1));
        const [statsRes, ledgerRes, detailRes] = await Promise.all(tasks);
        if (cancelled) return;
        setBreakdown(statsRes || null);
        setLedger(ledgerRes || { content: [], totalElements: 0, totalPages: 0 });
        if (detailRes) setAccount(detailRes?.account || null);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e?.message || "明细加载失败（统计接口可能未上线）");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to, dimension, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const changePage = async (nextPage) => {
    setError("");
    try {
      setPage(nextPage);
      await loadLedger(nextPage);
    } catch (e) {
      console.error(e);
      setError(e?.message || "流水加载失败");
    }
  };

  const runAction = async (action, successText) => {
    setActionLoading(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(successText);
      await loadLedger(page);
      return true;
    } catch (e) {
      console.error(e);
      setError(e?.message || "积分操作失败");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const submitGrant = async () => {
    if (!grantForm.amount || Number(grantForm.amount) <= 0) {
      setError("临时加额的积分必须大于 0");
      return;
    }
    const ok = await runAction(
      () => grantUserPoints(key, grantForm),
      `已向 ${displayUserId(key)} 发放 ${formatPoints(grantForm.amount)} 永久积分`
    );
    if (ok) setGrantForm({ amount: "", note: "", requestId: crypto.randomUUID() });
  };

  const submitAdjust = async () => {
    if (!adjustForm.delta || Number(adjustForm.delta) === 0) {
      setError("调整值不能为 0，正数增加、负数扣减");
      return;
    }
    const ok = await runAction(
      () => adjustUserPoints(key, adjustForm),
      `已校正 ${displayUserId(key)} 的永久积分`
    );
    if (ok) setAdjustForm({ delta: "", note: "", requestId: crypto.randomUUID() });
  };

  // 分项合计（按类型）
  const breakdownGroups = (breakdown?.groups || []).filter((g) => Number(g.points || 0) > 0 || Number(g.count || 0) > 0);
  const breakdownTotal = breakdownGroups.reduce((acc, g) => acc + Number(g.points || 0), 0);

  // 每日额度进度
  const dailyQuota = Number(account?.dailyQuota || 0);
  const dailyRemaining = account?.dailyRemaining != null ? Number(account.dailyRemaining) : null;
  const dailyUsed = dailyQuota > 0 && dailyRemaining != null ? Math.max(0, dailyQuota - dailyRemaining) : null;
  const dailyPct = dailyUsed != null ? Math.min(100, (dailyUsed / dailyQuota) * 100) : null;

  const ledgerRows = ledger?.content || [];
  const totalPages = ledger?.totalPages || 0;

  return (
    <div
      className="fixed inset-0 animate-in fade-in duration-150"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: "var(--z-modal)" }}
      onClick={onClose}
    >
      <aside
        className="ml-auto h-full w-full max-w-xl border-l overflow-y-auto custom-scrollbar animate-in slide-in-from-right-4 duration-200"
        style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-secondary)", boxShadow: "var(--shadow-modal)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="h-16 px-5 flex items-center justify-between border-b sticky top-0" style={{ borderColor: "var(--color-border-secondary)", backgroundColor: "var(--color-bg-primary)" }}>
          <div className="min-w-0">
            <div className="text-label">{DIMENSIONS.find((d) => d.key === dimension)?.label} · {range.from} ~ {range.to}</div>
            <div className="text-lg font-semibold truncate mt-0.5">{name}</div>
          </div>
          <IconAction title="关闭" onClick={onClose} icon={X} />
        </header>

        <div className="p-5 space-y-5">
          {error && <ErrorNotice message={error} />}
          {message && (
            <div
              className="rounded-lg px-3.5 py-2.5 text-sm animate-in fade-in slide-in-from-top-1 duration-200"
              style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 12%, transparent)", border: "1px solid var(--color-success)", color: "var(--color-success)" }}
            >
              {message}
            </div>
          )}

          {/* 分项合计 */}
          <section>
            <h4 className="text-sm font-semibold mb-3">分项合计（按生成类型）</h4>
            {loading && !breakdown ? (
              <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="skeleton h-8" />)}</div>
            ) : !breakdownGroups.length ? (
              <EmptyText text="该时段内没有分项数据" />
            ) : (
              <div className="space-y-2.5">
                {breakdownGroups.map((g) => {
                  const share = breakdownTotal > 0 ? Number(g.points || 0) / breakdownTotal : 0;
                  return (
                    <div key={g.key} className="flex items-center gap-3">
                      <span className="w-10 shrink-0 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        {TYPE_LABELS[g.key] || g.key}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                        <div className="h-full rounded-full" style={{ width: `${Math.max(2, share * 100)}%`, backgroundColor: ACCENT, opacity: 0.8 }} />
                      </div>
                      <span className="font-mono-num text-xs w-24 text-right shrink-0">
                        {formatPoints(g.points)} 分 · {formatMoney(g.costCny)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 每日额度（仅用户维度） */}
          {isUser && (
            <section className="border-t pt-4" style={{ borderColor: "var(--color-border-secondary)" }}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">今日免费额度</h4>
                {dailyPct != null && (
                  <span className="font-mono-num text-2xs" style={{ color: dailyPct >= 100 ? "var(--color-error)" : "var(--color-text-muted)" }}>
                    {formatPoints(dailyUsed)} / {formatPoints(dailyQuota)} · {dailyPct.toFixed(0)}%
                  </span>
                )}
              </div>
              {dailyPct == null ? (
                <EmptyText text="额度信息不可用" />
              ) : (
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(dailyUsed > 0 ? 2 : 0, dailyPct)}%`, backgroundColor: dailyPct >= 100 ? "var(--color-error)" : ACCENT }}
                  />
                </div>
              )}
            </section>
          )}

          {/* 流水列表 */}
          <section className="border-t pt-4" style={{ borderColor: "var(--color-border-secondary)" }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold">积分流水</h4>
              <span className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>
                共 {ledger?.totalElements ?? ledgerRows.length} 条
              </span>
            </div>
            {loading && !ledger ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-8" />)}</div>
            ) : !ledgerRows.length ? (
              <EmptyText text="该时段内没有流水记录" />
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border" style={{ borderColor: "var(--color-border-secondary)" }}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-label text-left">
                        <th className="font-medium px-3 py-2">时间</th>
                        <th className="font-medium px-3 py-2">类型</th>
                        <th className="font-medium px-3 py-2">变动</th>
                        <th className="font-medium px-3 py-2">余额</th>
                        <th className="font-medium px-3 py-2">费用</th>
                        <th className="font-medium px-3 py-2">项目</th>
                        <th className="font-medium px-3 py-2">备注</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((item) => (
                        <tr key={item.id} style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                          <td className="px-3 py-2 text-2xs font-mono-num whitespace-nowrap" style={{ color: "var(--color-text-muted)" }}>
                            {formatDateTime(item.createdAt)}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{LEDGER_LABELS[item.reason] || item.reason || "-"}</td>
                          <td className="px-3 py-2 text-xs font-mono-num font-semibold whitespace-nowrap" style={{ color: Number(item.delta) < 0 ? "var(--color-error)" : "var(--color-success)" }}>
                            {formatSignedPoints(item.delta)}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono-num whitespace-nowrap">{formatPoints(item.balanceAfter)}</td>
                          <td className="px-3 py-2 text-xs font-mono-num whitespace-nowrap">{item.costCny != null ? formatMoney(item.costCny) : "-"}</td>
                          <td className="px-3 py-2 text-xs max-w-28 truncate" title={projectNameOf(item.projectId)}>{projectNameOf(item.projectId)}</td>
                          <td className="px-3 py-2 text-xs max-w-40 truncate" title={item.note || item.remark || ""}>{item.note || item.remark || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <SecondaryButton onClick={() => changePage(page - 1)} disabled={page <= 0}>上一页</SecondaryButton>
                    <span className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>{page + 1} / {totalPages}</span>
                    <SecondaryButton onClick={() => changePage(page + 1)} disabled={page + 1 >= totalPages}>下一页</SecondaryButton>
                  </div>
                )}
              </>
            )}
          </section>

          {/* 临时加额 / 调整（仅用户维度，复用现有发放交互） */}
          {isUser && (
            <section className="border-t pt-4" style={{ borderColor: "var(--color-border-secondary)" }}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <Plus size={14} style={{ color: ACCENT }} />
                    <h4 className="text-sm font-semibold">临时加额</h4>
                  </div>
                  <div className="space-y-2.5">
                    <FilterInput label="积分数量" type="number" min="1" step="1" value={grantForm.amount} onChange={(v) => setGrantForm((c) => ({ ...c, amount: v }))} />
                    <FilterInput label="备注" value={grantForm.note} onChange={(v) => setGrantForm((c) => ({ ...c, note: v }))} />
                    <PrimaryButton onClick={submitGrant} disabled={actionLoading}>发放永久积分</PrimaryButton>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <Pencil size={14} style={{ color: "var(--color-blue-primary)" }} />
                    <h4 className="text-sm font-semibold">积分调整</h4>
                  </div>
                  <div className="space-y-2.5">
                    <FilterInput label="调整值（正加负减）" type="number" step="1" value={adjustForm.delta} onChange={(v) => setAdjustForm((c) => ({ ...c, delta: v }))} />
                    <FilterInput label="备注" value={adjustForm.note} onChange={(v) => setAdjustForm((c) => ({ ...c, note: v }))} />
                    <SecondaryButton onClick={submitAdjust} disabled={actionLoading}>应用调整</SecondaryButton>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
};

// ============================
// 排行表
// ============================

const SPARKLINE_ROW_LIMIT = 12;

const PointsRankingPane = ({ projectOptions = [] }) => {
  const [dimension, setDimension] = useState("user");
  const [range, setRange] = useState(() => rangeOfPreset("30d"));
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // 搜索防抖：避免每次击键都重算 rows 并触发 sparkline 流程
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [sparklines, setSparklines] = useState({});
  const [selected, setSelected] = useState(null);
  const requestSeq = useRef(0);
  const sparkCache = useRef(new Map());

  const projectNameOf = useCallback((id) => {
    if (id == null || id === "") return "-";
    const found = (projectOptions || []).find((p) => String(p.id) === String(id));
    return found?.name || `项目 ${id}`;
  }, [projectOptions]);

  const nameOf = useCallback((keyValue) => {
    if (dimension === "user") return displayUserId(keyValue, "未归属");
    if (dimension === "project") return projectNameOf(keyValue);
    return TYPE_LABELS[keyValue] || keyValue || "-";
  }, [dimension, projectNameOf]);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const data = await fetchPointStats({ from: range.from, to: range.to, granularity: "day", dimension });
      if (seq !== requestSeq.current) return;
      setStats(data || null);
      setLoadedOnce(true);
    } catch (e) {
      console.error(e);
      if (seq === requestSeq.current) setError(e?.message || "排行数据加载失败（统计接口可能未上线）");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [dimension, range]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = (stats?.groups || []).map((row) => ({
      key: row.key,
      points: Number(row.points || 0),
      costCny: Number(row.costCny || 0),
      count: Number(row.count || 0),
      videoCount: Number(row.videoCount || 0),
      imageCount: Number(row.imageCount || 0),
      textCount: Number(row.textCount || 0),
      gacha: row.gacha || null,
    }));
    list.sort((a, b) => b.points - a.points);
    const q = debouncedSearch.toLowerCase();
    if (!q) return list;
    return list.filter((row) => String(row.key || "").toLowerCase().includes(q) || nameOf(row.key).toLowerCase().includes(q));
  }, [stats, debouncedSearch, nameOf]);

  // 前 N 行的迷你趋势（按用户/项目维度逐行拉取日序列，带缓存；类型维度无过滤参数，显示 -）
  // 串行限速加载：逐行渐进填充，避免 12 个统计请求并发打满后端（统计接口含重聚合 SQL）
  useEffect(() => {
    if (dimension === "type") {
      setSparklines({});
      return undefined;
    }
    const targets = rows.slice(0, SPARKLINE_ROW_LIMIT);
    if (!targets.length) return undefined;
    let cancelled = false;
    const cacheKey = (k) => `${dimension}|${k}|${range.from}|${range.to}`;

    // 先铺缓存命中的数据，未命中的显示占位
    const base = {};
    targets.forEach((row) => { base[row.key] = sparkCache.current.get(cacheKey(row.key)) || null; });
    setSparklines(base);

    const missing = targets.filter((row) => !sparkCache.current.has(cacheKey(row.key)));
    (async () => {
      for (const row of missing) {
        if (cancelled) return;
        try {
          const filter = dimension === "user" ? { userId: row.key } : { projectId: row.key };
          const data = await fetchPointStats({ from: range.from, to: range.to, granularity: "day", dimension, ...filter });
          sparkCache.current.set(cacheKey(row.key), (data?.series || []).map((s) => Number(s.points || 0)));
        } catch {
          sparkCache.current.set(cacheKey(row.key), []);
        }
        if (cancelled) return;
        const key = row.key;
        setSparklines((current) => ({ ...current, [key]: sparkCache.current.get(cacheKey(key)) || [] }));
        // 串行限速：每个请求间隔 250ms，给后端留喘息
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    })();
    return () => { cancelled = true; };
  }, [rows, dimension, range]);

  return (
    <div className="space-y-4">
      {/* 控制条 */}
      <div
        className="rounded-lg border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-3"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}
      >
        <Segmented options={DIMENSIONS} value={dimension} onChange={setDimension} ariaLabel="排行维度" />
        <DateRangeControl from={range.from} to={range.to} onRangeChange={setRange} />
        <div className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-muted)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={dimension === "user" ? "搜索用户" : dimension === "project" ? "搜索项目" : "搜索类型"}
              className="h-8 w-44 rounded-md pl-7 pr-2.5 text-xs outline-none"
              style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
            />
          </div>
          <button
            type="button"
            onClick={load}
            title="刷新"
            className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && <ErrorNotice message={error} />}

      <Panel
        title={`消耗排行 · ${DIMENSIONS.find((d) => d.key === dimension)?.label}`}
        sub={`${range.from} ~ ${range.to} · 点击行查看明细`}
        right={<span className="font-mono-num text-2xs" style={{ color: "var(--color-text-muted)" }}>{rows.length} 行</span>}
      >
        {!loadedOnce && loading ? (
          <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-9" />)}</div>
        ) : !rows.length ? (
          <EmptyBlock text="该时段内没有排行数据" hint="调整时段或切换维度" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label text-left">
                  <th className="font-medium pb-2.5 pr-4 w-10">排名</th>
                  <th className="font-medium pb-2.5 pr-4">名称</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">积分消耗</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">真实花销</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">次数</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">抽卡率</th>
                  <th className="font-medium pb-2.5 text-right">趋势</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const gacha = row.gacha;
                  return (
                    <tr
                      key={row.key ?? idx}
                      onClick={() => setSelected({ dimension, key: row.key, name: nameOf(row.key) })}
                      className="hover:bg-[var(--color-bg-tertiary)] transition-colors cursor-pointer"
                      style={{ borderTop: "1px solid var(--color-border-secondary)" }}
                    >
                      <td className="py-2.5 pr-4 font-mono-num text-2xs" style={{ color: idx < 3 ? ACCENT : "var(--color-text-muted)" }}>
                        {String(idx + 1).padStart(2, "0")}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-medium max-w-56 truncate" title={nameOf(row.key)}>{nameOf(row.key)}</td>
                      <td className="py-2.5 pr-4 text-xs font-semibold font-mono-num text-right whitespace-nowrap" style={{ color: ACCENT }}>
                        {formatPoints(row.points)}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num text-right whitespace-nowrap">{formatMoney(row.costCny)}</td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num text-right whitespace-nowrap">{formatPoints(row.count)}</td>
                      <td
                        className="py-2.5 pr-4 text-xs font-mono-num text-right whitespace-nowrap"
                        title={gacha ? `抽 ${gacha.draws ?? 0} 次 / 采用 ${gacha.accepted ?? 0} 段 · 废卡成本 ${formatMoney(gacha.wasteCostCny)}` : "暂无抽卡数据"}
                      >
                        {gacha ? formatRate(gacha.rate) : "-"}
                      </td>
                      <td className="py-2.5 text-right">
                        <span className="inline-block"><Sparkline data={sparklines[row.key]} /></span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {selected && (
        <RankingDrawer
          target={selected}
          range={range}
          projectNameOf={projectNameOf}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default PointsRankingPane;
